import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import Head from 'next/head';
import { graphql } from '@apollo/client/react/hoc';
import Typography from '@material-ui/core/Typography';
import compose from 'lodash/flowRight';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/error-message';
import NotFound from '../../../components/not-found';
import EventList from '../../../components/event-list';
import { userPastQuery } from '../../../apollo/users';
import { buildTitle } from '../../../utils/layout';
import config from '../../../config';
import UserHeader from '../../../components/users/header';
import EventsGrid from '../../../components/users/events-grid';
import consumeAppContext from '../../../consumers/app';
import Main from '../../../components/main';

const buildJsonLdPerson = (user) => {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": user.displayName,
    "url": `${config().universal.baseUri}/people/${user.username}`,
    "image": user.avatar.large.url,
  };
  return ld;
};

const PastPerson = ({ user, error, loading }) => {
  if (error) {
    return <ErrorMessage message='An error occurred while loading user.' error={error} />;
  }
  if (loading) { return <Loading />; }
  if (!user) { return <NotFound />; }

  return (
    <Main>
      <Head>
        <title>{buildTitle(`Past Events - ${user.displayName}`)}</title>
        {!user.discoverable && (
          <meta name="robots" content="noindex" />
        )}
        <link rel="canonical" href={`${config().universal.baseUri}/people/${user.username}/past`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildJsonLdPerson(user))
          }}
        />
        <meta property="og:url" content={`${config().universal.baseUri}/people/${user.username}/past`} />
        <meta property="og:title" content={`${user.displayName} Past Bike Rides and Events`} />
        <meta property="og:image" content={user.avatar.large.url} />
        <meta property="og:type" content="profile" />
        <meta property="profile:first_name" content={user.givenName} />
        <meta property="profile:last_name" content={user.familyName} />
        <meta property="profile:username" content={user.username} />
      </Head>

      <UserHeader user={user} />

      <EventsGrid user={user} tab="past">
        <Typography variant="h2">Past</Typography>
        <EventList
          events={user.pastHostedEvents}
          emptyMessage="No past public events"
          fullDate
        />
      </EventsGrid>
    </Main>
  );
}

PastPerson.propTypes = {
  router: PropTypes.object,
  user: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object,
  loggedInUser: PropTypes.object
};

export default compose(
  withRouter,
  consumeAppContext,
  graphql(userPastQuery, {
    props: ({ data: { user, loading, error } }) => {
      return {
        user,
        loading: loading,
        error: error
      };
    },
    options: ({ router }) => ({
      variables: {
        username: router.query.username
      }
    })
  })
)(PastPerson);
